import cloudflareAssets from "@/assets/cloudflareAssets";
import logoSmall from "@/assets/images/logoSmall.svg";
import { ActivePaths } from "@/shared/common/constants";
import { AppPaths, CloudflareAsset, type FeatureToggle } from "@/shared/common/enums";
import type {
  ISidebarMenuItem,
  ISidebarMenuItemChild,
  ISidebarMenuParam,
} from "@/shared/common/types";
import { CloudFlareImage, LanguageSelect } from "@/shared/components";
import { buildRoute } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { NavigationItem, SideNavigation } from "@ab.uitools/ui-library/components/SideNavigation";
import {
  NavigationItemTypes
} from "@ab.uitools/ui-library/components/SideNavigation/NavigationItem/types";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useOnOutsideClick } from "@ab.uitools/ui-library/hooks";
import classNames from "classnames";
import { type SyntheticEvent, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { sidebarMenuItem } from "./SidebarMenuItem";

interface Props {
  className?: string;
  onOpen?: (isOpen: boolean) => void;
  isOpen?: boolean;
  onClose?: () => void;
  isPined?: boolean;
  additionalRef?: HTMLElement | null;
}

export const SidebarMenu = ({
  onOpen,
  isOpen,
  onClose,
  isPined,
  className = "",
  additionalRef = null,
}: Props) => {
  const { t } = useTranslation();
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { toggles, isLoaded } = useAppSelector((state) => state.featureToggle);

  const menuItems = useMemo(() => {
    if (!isLoaded) return [];
    return sidebarMenuItem
      .filter((item) => {
        if (!item.item?.featureFlag) return true;
        return toggles?.[item.item.featureFlag as FeatureToggle];
      })
      .map((item) => {
        if (!item.group) return item;
        return {
          ...item,
          group: {
            ...item.group,
            children: item.group.children?.filter((child) => {
              if (!child.featureFlag) return true;
              return toggles?.[child.featureFlag as FeatureToggle];
            }),
          },
        };
      });
  }, [isLoaded, toggles]);


  const isActivePath = (path: string | string[]) => {
    if (Array.isArray(path)) {
      return path.some(isActivePath);
    }
    return location.pathname?.includes(path);
  };

  const handleChangeRoute = (e: SyntheticEvent, href: string, params?: ISidebarMenuParam) => {
    e.stopPropagation();
    const url = buildRoute(href, { ...params });
    navigate(url);
    if (!isDesktop) {
      onClose?.();
    }
  };

  const isDesktop = useMediaQuery();

  const closeSidebarOnOutsideClick = () => {
    if (!isDesktop) {
      onClose?.();
    }
  };

  useOnOutsideClick([menuRef, additionalRef], closeSidebarOnOutsideClick, !!isOpen, useId());

  return (
    <div
      ref={setMenuRef}
      className={
        classNames("sidebar-container", {
          [`sidebar-pinned`]: isDesktop ? isPined : true,
          [`sidebar-unpinned`]: isDesktop ? !isPined : false,
          [className]: className,
        })
      }
    >
      <div className="sidebar--wrapper">
        <div
          className={
            classNames("sidebar-menu", {
              [`sidebar-menu--open`]: isOpen,
              [`sidebar-menu--close`]: !isOpen,
            })}
        >
          <SideNavigation
            actionElm={<p></p>}
            logo={!isDesktop ? "" : cloudflareAssets[CloudflareAsset.MyAmeriaLogo]}
            isOpen={isOpen}
            logoClosed={logoSmall}
            logoUrl={AppPaths.Dashboard}
            onClose={onClose}
            onOpen={onOpen}
            isPined
          >
            <main className={"scrollbar scrollbar--vertical"}>
              <NavigationItem
                As={() => (
                  <div
                    onClick={(e) => handleChangeRoute(e, AppPaths.Dashboard)}
                  >
                    <CloudFlareImage path={CloudflareAsset.Main}/>
                    {isOpen && (
                      <Text className="pl-10" weight="semibold">
                        {t("sidebar.main")}
                      </Text>
                    )}
                  </div>
                )}
                active={isActivePath(ActivePaths.Dashboard)}
                type={NavigationItemTypes.MAIN}
                isOpen={false}
              />
              {menuItems.map((menuItem: ISidebarMenuItem) => (
                <NavigationItem
                  key={menuItem.id}
                  expandIconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage
                        {...props}
                        path={CloudflareAsset.ArrowShortDownQuaternary}
                      />
                    ),
                    size: "large"
                  }}
                  As={() => (
                    <div
                      onClick={(e) => {
                        if (menuItem.item) {
                          handleChangeRoute(e, menuItem.item.href, menuItem.item.params);
                        }
                      }}
                    >
                      {menuItem.item?.icon?.Component && <menuItem.item.icon.Component/>}
                      {menuItem.group?.icon?.Component && <menuItem.group.icon.Component/>}
                      {isOpen && (
                        <Text className="pl-10" weight="semibold">
                          {t((menuItem?.item?.label || menuItem?.group?.label) as string)}
                        </Text>
                      )}
                    </div>
                  )}
                  type={NavigationItemTypes.MAIN}
                  isOpen={false}
                  active={
                    isActivePath(
                      menuItem?.group?.activePath || menuItem?.item?.activePath as string
                    )
                  }
                  expandable={!!menuItem?.group?.label}
                >
                  {menuItem?.group?.children?.map((child: ISidebarMenuItemChild) => (
                    <div
                      className="mt-2"
                      key={child.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <NavigationItem
                        expandIconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage
                              {...props}
                              path={CloudflareAsset.ArrowShortDownQuaternary}
                            />
                          ),
                          size: "large"
                        }}
                        As={() => (
                          <div
                            onClick={(e) => handleChangeRoute(e, child.href, child.params)}
                          >
                            {child.icon?.Component && <child.icon.Component/>}
                            <Text weight="semibold" className="navigation--item-text pl-10">
                              {t(child.label)}
                            </Text>
                          </div>
                        )}
                        active={isActivePath(child.activePath as string)}
                        type={NavigationItemTypes.SUB}
                        isOpen={false}
                      />
                    </div>
                  ))}
                </NavigationItem>
              ))}
            </main>
            <footer className="sidebar-footer">
              <div className="footer-actions-wrapper">
                <LanguageSelect isLightIcon/>
              </div>
            </footer>
          </SideNavigation>
        </div>
      </div>
    </div>
  );
};
